import { createRealtimeSessionId, countPresence } from '@/utils/supabase-realtime'

const presenceRegistry = new Map()

export function buildPagePresenceId (projectId, pageId, fallbackKey = 'page') {
  const safeProjectId = projectId ? String(projectId) : 'unknown'
  const safePageId = pageId ? String(pageId) : String(fallbackKey || 'page')

  return `project-${safeProjectId}_page-${safePageId}`
}

function getPresenceSessionId (pageId, userId) {
  if (typeof window === 'undefined') {
    return createRealtimeSessionId(userId || 'presence')
  }

  const storageKey = `builder-page-presence-session:${pageId}`
  const stored = window.localStorage.getItem(storageKey)

  if (stored) return stored

  const created = createRealtimeSessionId(userId || 'presence')
  window.localStorage.setItem(storageKey, created)
  return created
}

function notifyListeners (entry) {
  const nextCount = countPresence(entry.channel?.presenceState?.() || {})

  entry.onlineCount = nextCount
  entry.listeners.forEach((listener) => {
    listener(nextCount)
  })
}

function createPresenceEntry (supabase, pageId, userId) {
  const entry = {
    channel: null,
    consumers: 0,
    listeners: new Set(),
    onlineCount: 0
  }
  const sessionId = getPresenceSessionId(pageId, userId)

  entry.channel = supabase.channel(`presence:${pageId}`)
  entry.channel
    .on('presence', {event: 'sync'}, () => notifyListeners(entry))
    .on('presence', {event: 'join'}, () => notifyListeners(entry))
    .on('presence', {event: 'leave'}, () => notifyListeners(entry))
    .subscribe(async (status) => {
      if (status !== 'SUBSCRIBED') return

      try {
        await entry.channel.track({
          userId: userId || null,
          sessionId,
          joinedAt: new Date().toISOString()
        })
      } catch (err) {
        console.error(err)
      }
    })

  return entry
}

export function subscribeToPagePresence ({supabase, pageId, userId, onUpdate}) {
  if (!supabase || !pageId || typeof onUpdate !== 'function') {
    return () => {}
  }

  let entry = presenceRegistry.get(pageId)

  if (!entry) {
    entry = createPresenceEntry(supabase, pageId, userId)
    presenceRegistry.set(pageId, entry)
  }

  entry.consumers += 1
  entry.listeners.add(onUpdate)
  onUpdate(entry.onlineCount)

  return () => {
    const activeEntry = presenceRegistry.get(pageId)
    if (!activeEntry) return

    activeEntry.listeners.delete(onUpdate)
    activeEntry.consumers -= 1

    if (activeEntry.consumers > 0) return

    activeEntry.channel?.untrack?.().catch(() => {})
    supabase.removeChannel(activeEntry.channel)
    presenceRegistry.delete(pageId)
  }
}
